// OLMPS design tokens — CRISP WHITE NEUMORPHISM + GLASS.
//
// The look: one continuous NEUTRAL near-white material (#e9eaec). Zero blue
// tint — the old scheme was pale-blue and read as pastel/washed. Depth comes
// from DUAL shadows (neutral grey bottom-right + pure white highlight
// top-left). Raised = boxShadow pair. Pressed/carved = inset boxShadow pair.
//
// CRISP, not puffy: blur radius is kept close to the offset (~1.6x, not 2.5x)
// so edges read as a defined line cut into or extruded from the surface
// instead of a diffuse cloud. Every raised surface also carries a 1px top
// highlight rim — that hairline is what makes it look precise.
//
// GLASS: topmost/floating layers (sheets, modals, nav, overlays) use the
// `glass` export — translucent white fill + bright hairline rim + a wide soft
// ambient shadow. Pair with <BlurView> (expo-blur, already a dependency) for
// real backdrop blur; on web the `backdropFilter` in glass.web applies too.
//
// ONE accent — vivid blue — for primary actions and active states only.
// Semantic colours (success/warning/danger/info) and the `series` chart ramp
// live below so screens stop hand-rolling hex.
//
// RN 0.81 new-arch supports the CSS `boxShadow` style prop (multiple shadows,
// inset). Use the `neu` export below — do NOT hand-roll shadowColor/shadowOffset
// for surfaces; the single-shadow legacy `shadows` export stays only for old
// call sites and maps to soft neu-compatible values.
//
// Legacy key role-map (60+ call sites resolve to these — names kept, values new):
//   lime      → light "active/selected" tint. MUST stay light: many screens pair
//               it with dark `blueText` foregrounds.
//   limeSoft  → softer variant of the same.
//   gearPeach → secondary / inactive chip fill → base material.
//   purple    → data-series accent → muted indigo.
//   blueText  → dark navy ink accent. MUST stay dark (used as text on light
//               surfaces in ~55 files).

export const colors = {
  // ── Hero accent: vivid bright blue (primary buttons, active nav) ─────────
  // The ONLY chromatic colour in the chrome. Everything else is neutral.
  primary: '#2f80ff',
  primaryDeep: '#1e63e6',
  primaryGlow: 'rgba(47,128,255,0.42)',
  navActive: '#2f80ff',

  // ── Ink typography — near-black neutral, high contrast on white ──────────
  ink: '#14161a',
  inkSoft: '#5c6169',
  text: '#14161a',
  textMuted: '#767c86',
  textFaint: '#9ba1aa',

  // ── TONAL LADDER — NEUTRAL greys, every layer visibly lighter than the one
  //    it sits on, or nested surfaces become invisible:
  //      L0 outer chrome (chromeBg / base)       #e4e6e9  ← darkest
  //      L1 shell sheet / Atmosphere (baseLight)  #edeef1
  //      L2 panels (card — GlassPanel default)    #f5f6f7
  //      L3 rows/chips/tiles (glassFill)          #fafafb
  //      L4 modals/topmost (glassFillStrong)      #ffffff
  // Brighter overall (per feedback 2026-07-26): the whole ladder moved up
  // ~6-8 points of lightness so the material reads airy/bright rather than
  // grey. Edge CONTRAST comes from `shadowDark` growing stronger + tighter
  // (not from darkening the base) — see neu recipes below, which pair a
  // brighter fill with a crisper, higher-opacity dark shadow half so the cut
  // line stays sharp even though the material itself is lighter.
  base: '#eef0f2',        // L0 canonical background
  baseLight: '#f5f6f8',   // L1 shell sheet
  baseDark: '#dfe2e6',    // recessed wells / track grooves (darker than L0)
  shadowDark: 'rgba(148,155,167,0.68)', // dark half — stronger for sharper edges
  shadowLight: '#ffffff',               // light half

  // Old atmosphere keys — the L1 sheet with a whisper of gradient.
  chromeBg: '#eef0f2',
  sheetTop: '#fbfcfd',
  sheetMid: '#f5f6f8',
  sheetBottom: '#eef0f2',

  // ── Surface keys ────────────────────────────────────────────────────────
  glassFill: '#fcfdfd',        // L3: rows, chips, tiles, small cards
  glassFillStrong: '#ffffff',  // L4: modals, sheets, topmost surfaces
  glassStroke: 'rgba(255,255,255,0.90)',
  glassRim: 'rgba(255,255,255,0.85)',
  // Editable fields: white fill + a real visible outline so they clearly read
  // as "type here" against same-hue panels (fields must contrast).
  inputFill: '#ffffff',
  inputRim: 'rgba(120,128,140,0.38)',
  inputRimFocus: '#2f80ff',
  // The bare prompt+field+send rows (SyQi's clarify question, CLIO's journal
  // entry) have no wrapper card, so the field's own fill carries its state:
  // red while it's still empty and owed an answer, green once something's in
  // it. PromptField pulses Empty→EmptyPulse while waiting, then settles on
  // Filled. All four stay pale — dark body text sits on top of them.
  inputFillEmpty: '#f2938c',
  inputFillEmptyPulse: '#e5645a',
  inputRimEmpty: '#b3382f',
  inputFillFilled: '#b6e7c4',
  inputRimFilled: '#2e8650',
  // Muted placeholder for those saturated fills — the usual grey textMuted
  // goes muddy on red, so this is a dark tint of the fill instead.
  inputPlaceholderOnFill: 'rgba(38,8,4,0.78)',
  card: '#f5f6f7',   // L2: containing panels (GlassPanel default fill)
  cardBorder: 'rgba(255,255,255,0.90)',
  glass: '#f5f6f7',
  overlay: 'rgba(20,22,26,0.38)',

  // ── Hairline borders — the "crisp line" of the scheme ────────────────────
  border: 'rgba(150,157,168,0.22)',
  borderStrong: 'rgba(150,157,168,0.40)',
  rimLight: 'rgba(255,255,255,0.95)',

  // ── Semantic (stop hand-rolling these in screens) ────────────────────────
  success: '#1f9d55',
  warning: '#d97706',
  info: '#2f80ff',

  // ── Legacy keys (values refreshed, roles preserved) ──────────────────────
  lime: '#dbe8ff',                    // light active tint — dark text stays legible
  limeSoft: 'rgba(219,232,255,0.8)',
  blue: '#2f80ff',                    // strong active state
  blueText: '#1a4fd6',                // vivid dark-blue ink accent — keep dark
  blueGlow: 'rgba(47,128,255,0.5)',
  gearPeach: '#f5f6f7',
  slate: '#767c86',
  purple: '#3f8cf2',                  // data-series accent — kept blue, not purple

  bgTop: '#f2f3f5',
  bgBottom: '#e4e6e9',

  danger: '#dc2626',
  white: '#ffffff',
};

/** Chart / data-series ramp. Use series[i % series.length] — never inline hex. */
export const series = [
  '#2f80ff', '#0d9488', '#7c3aed', '#d97706',
  '#e11d48', '#1f9d55', '#0891b2', '#c026d3',
] as const;

/** Display font — every title bar / screen title / section title in the app
 *  uses this serif, always rendered in caps (`textTransform: 'uppercase'`
 *  alongside `fontFamily: fonts.title`). Loaded via useFonts in App.tsx. */
export const fonts = {
  title: 'ZeraphineObsidianSerif-Regular',
} as const;

export const radii = {
  shell: 34,
  nav: 30,
  tile: 18,
  panel: 24,
  pill: 999,
};

export const sizes = {
  gearTile: 44,
  navShape: 56,
  navBar: 88,
  hexagon: 28,
  panelBorder: 0, // neumorphism has NO borders — edges come from shadow pairs
};

// ── Neumorphic boxShadow recipes (RN 0.81 new-arch `boxShadow` prop) ────────
// Spread these into styles: { ...neu.raised } → { boxShadow: '…' }.
export const neu = {
  /** Standard raised card / panel. Crisper still: blur ≈ 1.3× offset, darker
   *  edge (0.68 vs old 0.55) so the cut reads sharp against the brighter
   *  material, + hairline rim. */
  raised: {
    boxShadow:
      `4px 4px 5px rgba(148,155,167,0.68), -4px -4px 5px #ffffff, inset 0 1px 0 rgba(255,255,255,1)`,
  },
  /** Big hero panels / the app shell. */
  raisedLg: {
    boxShadow:
      `7px 7px 9px rgba(148,155,167,0.65), -7px -7px 9px #ffffff, inset 0 1px 0 rgba(255,255,255,1)`,
  },
  /** Small chips, icon buttons, tiles. */
  raisedSm: {
    boxShadow:
      `2px 2px 3px rgba(148,155,167,0.70), -2px -2px 3px #ffffff, inset 0 1px 0 rgba(255,255,255,0.95)`,
  },
  /** Pressed / selected state — surface pushed into the material. */
  inset: {
    boxShadow:
      `inset 3px 3px 4px rgba(148,155,167,0.65), inset -3px -3px 4px #ffffff`,
  },
  /** A sharp groove cut into the surface — dividers, wells, track grooves. */
  carved: {
    boxShadow:
      `inset 1px 1px 2px rgba(148,155,167,0.70), inset -1px -1px 2px rgba(255,255,255,1)`,
  },
  /** Text inputs — plain white box with a visible outline (per Joel 2026-07-19:
   *  the inner-shadow recess on editable fields was distracting; keep inputs
   *  flat + outlined until further notice. `inset` above still styles pressed
   *  chips/toggles). */
  insetDeep: {
    boxShadow: 'none',
    borderWidth: 1,
    borderColor: 'rgba(120,140,175,0.55)',
  },
  /** Accent glow under primary / active elements (pair with raisedSm). */
  glowPrimary: {
    boxShadow:
      `3px 5px 12px ${colors.primaryGlow}, -2px -2px 6px rgba(255,255,255,0.75)`,
  },
} as const;

// Legacy single-shadow export — kept for old call sites; values softened to sit
// alongside the neu system. New code should use `neu` above.
export const shadows = {
  nav: {
    shadowColor: '#a3b1c6',
    shadowOpacity: 0.5,
    shadowRadius: 16,
    shadowOffset: { width: 6, height: 8 },
    elevation: 8,
  },
  shell: {
    shadowColor: '#a3b1c6',
    shadowOpacity: 0.55,
    shadowRadius: 26,
    shadowOffset: { width: 10, height: 14 },
    elevation: 12,
  },
  glowActive: {
    shadowColor: '#4f74e8',
    shadowOpacity: 0.45,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
  },
  card: {
    shadowColor: '#a3b1c6',
    shadowOpacity: 0.5,
    shadowRadius: 14,
    shadowOffset: { width: 6, height: 6 },
    elevation: 4,
  },
  primary: {
    shadowColor: '#4f74e8',
    shadowOpacity: 0.4,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },
} as const;
